import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type {
  SearchEvent,
  SearchResult,
} from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput.types';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { TableRequestBody } from '@ipfabric/utils';
import { produce } from 'immer';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import type { ApiRequestParams } from 'utils/request/ApiRequestParams';
import { useApi } from 'utils/request/useApi';

const DEFAULT_PAGINATION_LIMIT = 15;
const DEFAULT_SEARCH_DEBOUNCE = 500;

interface Props<TRecord> {
  /**
   * Make a request on Tables API
   *
   * Mandatory stuff:
   * - URL
   * - column list
   * - filters
   *
   * Automatically added:
   * - method = POST
   * - pagination
   * - snapshot ID (if props.snapshot = true)
   *
   * Return value:
   * - ApiRequestParams when search query can be transformed to valid API query
   * - 'empty' when search query is considered empty ("Type to search" will be shown in dropdown)
   * - 'invalid' when search query cannot be transformed to valid request ("No results" will be shown
   *   in dropdown without actual query to the api)
   */
  createRequest: (
    searchEvent: SearchEvent,
  ) => ApiRequestParams<TableRequestBody<TRecord>> | 'empty' | 'invalid';
  debounced?: boolean | number;
  fetchWithEmptyQuery?: boolean;
  /** whether or not given table endpoint request requires snapshot id */
  snapshot: boolean;
  /** default DEFAULT_PAGINATION_LIMIT */
  paginationLimit?: number;
}

export interface ApiTableAutocompleteHook<TRecord> {
  /**
   * Null = result of empty search query
   *
   * We should differentiate from empty search result to set proper caption
   * to empty dropdown.
   */
  content: TRecord[] | null;
  loading: boolean;
  search: (searchEvent: SearchEvent) => Promise<SearchResult<TRecord>>;
}

export const useApiTableAutocomplete = <TRecord,>(
  props: Props<TRecord>,
): ApiTableAutocompleteHook<TRecord> => {
  const paginationLimit = props.paginationLimit ?? DEFAULT_PAGINATION_LIMIT;

  const api = useApi();
  const snapshotId = useSnapshotId();

  const [loading, setLoading] = useLoadingState();
  const [content, setContent] = useState<TRecord[] | null>(null);
  const [totalRowsCount, setTotalRowsCount] = useState<number | null>(null);
  const [totalPageCount, setTotalPageCount] = useState<number | null>(null);

  const search = useStableCallback(
    async (searchEvent: SearchEvent): Promise<SearchResult<TRecord>> => {
      if (
        searchEvent.nextPageNumber !== 0 &&
        totalRowsCount &&
        totalRowsCount <= (content?.length ?? 0)
      ) {
        return {
          currentPage: searchEvent.nextPageNumber,
          totalRowsCount,
          totalPageCount,
          paginationLimit,
          content,
        };
      }

      if (searchEvent.inputValue === '' && !props.fetchWithEmptyQuery) {
        setContent(null);
        setTotalRowsCount(0);
        return {
          currentPage: searchEvent.nextPageNumber,
          totalRowsCount: 0,
          totalPageCount: 0,
          paginationLimit,
          content: null,
        };
      }

      if (props.snapshot && snapshotId === null) {
        stumble('No snapshot selected');
        return {
          currentPage: searchEvent.nextPageNumber,
          totalRowsCount: 0,
          totalPageCount: 0,
          paginationLimit,
          content,
        };
      }

      const requestStub = props.createRequest(searchEvent);
      if (requestStub === 'empty') {
        setContent(null);
        setTotalRowsCount(null);
        return {
          currentPage: searchEvent.nextPageNumber,
          totalRowsCount: null,
          totalPageCount: null,
          paginationLimit,
          content: null,
        };
      }
      if (requestStub === 'invalid') {
        const emptyContent: TRecord[] = [];
        setContent(emptyContent);
        setTotalRowsCount(0);
        return {
          currentPage: searchEvent.nextPageNumber,
          totalRowsCount: 0,
          totalPageCount: 0,
          paginationLimit,
          content: emptyContent,
        };
      }

      return await withLoadingIndicator(setLoading)(async () => {
        const request = produce(requestStub, (draft) => {
          draft.method = 'POST';
          // data must be present in valid table request
          draft.data!.pagination = {
            start: paginationLimit * searchEvent.nextPageNumber,
            limit: paginationLimit,
          };
          if (props.snapshot) {
            draft.data!.snapshot = snapshotId!;
          }
        });
        const response = await api.request(request);
        const pageCount = Math.ceil(response._meta.count / paginationLimit);
        const results =
          searchEvent.nextPageNumber === 0
            ? response.data
            : [...(content ?? []), ...response.data];

        setTotalRowsCount(response._meta.count);
        setTotalPageCount(pageCount);
        setContent(results);

        return {
          currentPage: searchEvent.nextPageNumber,
          totalRowsCount: response.totalRowsCount,
          totalPageCount: pageCount,
          paginationLimit,
          content: results,
        };
      });
    },
  );

  // This is a trailing and leading debounce, which will only throttle/debounce calls after the first one
  const debouncedSearch = useMemo(
    () =>
      props.debounced
        ? _.debounce(
            search,
            typeof props.debounced === 'number'
              ? props.debounced
              : DEFAULT_SEARCH_DEBOUNCE,
            { leading: true, trailing: true },
          )
        : search,
    [props.debounced],
  );

  return {
    content,
    loading: loading === 'LOADING',
    search: debouncedSearch,
  };
};
